import { createAction } from 'redux-actions';

import { tapFlashError, tapFlashSuccess } from '../generic/flash-messages';
import changeRequestInProgress from '../generic/request-in-progress/actions';
import api from '../REST/api';
import createUiAction from '../utils/create-ui-action';
import { EDITOR_NAME } from './connect-editor';
import getId from './utils/get-id';
import normalizeQuote from './utils/normalize-quote';
import transformForPatch from './utils/transform-for-patch';

export const changeQuote = createAction(
  'quote-editor/change-quote',
  (name, value) => ({ name, value }),
  (name, value, isUiAction = false) => ({ uiAction: isUiAction }),
);

export const changeQuoteLine = createAction(
  'quote-editor/change-quote-line',
  (_, name, value) => ({
    name,
    value,
  }),
  quoteLineId => ({ quoteLineId }),
);

export const changeQuoteLineSelection = createAction(
  'quote-editor/change-quote-line-selection',
  (_, selected) => selected,
  quoteLineId => ({ quoteLineId }),
);

export const changeQuoteLineGender = createAction(
  'quote-editor/change-quote-line-gender',
  (_, genders) => ({
    genders,
  }),
  (quoteLineId, _, product) => ({ quoteLineId, product }),
);

export const changeCountrySplit = createAction(
  'quote-editor/change-country-split',
  (_, countryCode, value) => ({
    countryCode,
    value,
  }),
  quoteLineId => ({ quoteLineId }),
);

export const splitCountrySharesEqually = createAction(
  'quote-editor/split-country-shares-equally',
  quoteLineId => ({ quoteLineId }),
);

export const copyCountrySplit = createAction(
  'quote-editor/copy-country-split',
  (sourceCountrySplit, targetQuoteLineIds) => ({
    sourceCountrySplit,
    targetQuoteLineIds,
  }),
);

export const addQuoteLine = createAction('quote-editor/add-quote-line');

export const removeQuoteLine = createAction(
  'quote-editor/remove-quote-line',
  null,
  id => ({ quoteLineId: id }),
);

export const duplicateQuoteLine = createAction(
  'quote-editor/duplicate-quote-line',
  () => ({ id: getId() }),
  id => ({ quoteLineId: id }),
);

export const storeSavedQuote = createAction(
  'quote-editor/saved-quote',
  savedQuote => ({ savedQuote }),
);

export const submit = initialQuote => (dispatch, getState) => {
  const { quote, quoteLinesById, productsById, opportunity, savedQuote } =
    getState()[EDITOR_NAME];

  // quoteToCompare is used to compared the initial quote with
  // the current quote changes, to send only the changed fields to BE.
  // After save, quoteToCompare will be the previous saved quote
  // so the process can be repeated with an updated quote instead.
  const quoteToCompare =
    (savedQuote ? normalizeQuote(savedQuote) : null) || initialQuote;

  return dispatch({
    type: 'quote-editor/submit',
    payload: api
      .patchQuote({
        id: quote.id,
        data: transformForPatch({
          quote,
          quoteLinesById,
          productsById,
          opportunity,
          initialQuote: quoteToCompare,
        }),
      })
      .then(updatedQuote => {
        dispatch(storeSavedQuote(updatedQuote));
        return normalizeQuote(updatedQuote);
      })
      .then(
        tapFlashSuccess(dispatch, 'Quote update successful'),
        tapFlashError(dispatch),
      )
      .finally(() => {
        dispatch(changeRequestInProgress(false));
      }),
    meta: { resetModifiedOnSuccess: true },
  });
};

submit.toString = () => 'quote-editor/submit';

export const toggleCommercialIncentiveModal = createUiAction(
  'quote-editor/toggle-commercial-incentive-modal',
  isCommercialIncentiveModalVisible => ({ isCommercialIncentiveModalVisible }),
);

export const changeFilter = createUiAction(
  'quote-editor/change-filter',
  (name, value) => ({ name, value }),
);

export const filterQuoteLines = createUiAction(
  'quote-editor/filter-quote-lines',
  selectedFilters => ({ selectedFilters }),
);

export const applyFilters = (name, value) => (dispatch, getState) => {
  // Apply selections to filters
  if (name && value) dispatch(changeFilter(name, value));

  // Mark lines as filtered out based on filter selections
  const {
    quoteEditor: { quoteLineFilters },
  } = getState();
  dispatch(filterQuoteLines(quoteLineFilters));

  // Hide the filtered out lines
  const {
    quoteEditor: { quoteLinesById },
  } = getState();
  const filteredLines = Object.values(quoteLinesById).filter(
    line => !line.filteredOut,
  );
  dispatch(
    changeQuote(
      'quoteLines',
      filteredLines.map(line => line.id),
      true,
    ),
  );
};

applyFilters.toString = () => 'quote-editor/apply-filters';
